// src/pages/About.jsx
import React from "react";
import Container from "../components/Container.jsx";

// Reuse existing asset paths and palette from the project
const LOGO = "content/images/ethira-logo.png"; // used in Navbar.jsx
const HERO = "content/images/hero-image.jpg";  // used in Hero.jsx

export default function About() {
  return (
    <main className="min-h-screen bg-white text-[#003366]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt="City skyline" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0" />
        <Container className="relative py-24 sm:py-28 lg:py-32">
          <div className="max-w-3xl text-center mx-auto">
            <img src={LOGO} alt="Ethira logo" className="mx-auto h-14 w-auto drop-shadow" />
            <h1 className="mt-6 text-4xl sm:text-5xl font-extrabold tracking-tight text-[#003366] drop-shadow-lg">
              About Ethira Property Group
            </h1>
            <p className="mt-4 text-lg text-white/90 drop-shadow">
              We’re a boutique property investment consultancy built on ethics, transparency and long‑term relationships. Our promise is simple: clear, actionable advice that puts your interests first.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <a href="#/enquire" className="rounded-xl px-5 py-3 text-sm font-semibold bg-#003366 hover:bg-[#003366] text-white">Enquire now</a>
              <a href="#/guides" className="rounded-xl px-5 py-3 text-sm font-semibold bg-#003366 hover:bg-[#003366] text-white">Explore guides</a>
            </div>
          </div>
        </Container>
      </section>

      {/* What we do */}
      <section className="border-t border-slate-200">
        <Container className="py-16 lg:py-20">
          <h2 className="text-2xl font-semibold">What we do</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 p-6 shadow-sm bg-white">
              <h3 className="text-xl font-semibold">End‑to‑end investment support</h3>
              <p className="mt-3 text-[#003366]">
                We help you identify the right investments, secure finance, manage the legal process and prepare your buy‑to‑let, so you’re supported at every step from decision to completion and beyond.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6 shadow-sm bg-white">
              <h3 className="text-xl font-semibold">Aftercare and asset management</h3>
              <p className="mt-3 text-[#003366]">
                We provide thorough aftercare and ongoing property management—covering maintenance and tenant relations—so your asset keeps delivering without the hassle.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Why investors choose us */}
      <section className="border-t border-slate-200 bg-slate-50">
        <Container className="py-16 lg:py-20">
          <h2 className="text-2xl font-semibold">Why investors choose us</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {[{
              title: "Proven track record",
              body: "Advised on 80 locations with over £500m in total property value.",
            }, {
              title: "Disciplined sourcing",
              body: "We prioritise reputable partners and deep due diligence in locations with strong fundamentals.",
            }, {
              title: "Values you can measure",
              body: "Integrity, transparency and sustainability guide every decision and update.",
            }].map((c) => (
              <div key={c.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold">{c.title}</h3>
                <p className="mt-2 text-[#003366]">{c.body}</p>
                <span className="mt-4 inline-block h-1 w-16 rounded-full bg-slate-900/90" />
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* How we work */}
      <section className="border-t border-slate-200">
        <Container className="py-16 lg:py-20">
          <h2 className="text-2xl font-semibold">How we work</h2>
          <ol className="mt-8 grid gap-6 md:grid-cols-2">
            {[{
              step: "1",
              title: "Understand your goals",
              body: "We start with your financial objectives and risk profile, then map options clearly—without the sales spin.",
            }, {
              step: "2",
              title: "Select the right opportunities",
              body: "We present only investments that pass our checks and align with your plan.",
            }, {
              step: "3",
              title: "Execute seamlessly",
              body: "From mortgages to conveyancing, we coordinate trusted providers and keep everything moving.",
            }, {
              step: "4",
              title: "Stay with you post‑completion",
              body: "Aftercare and management keep your investment on track for the long term.",
            }].map((i) => (
              <li key={i.step} className="rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold bg-[#003366] text-white">
                    {i.step}
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold">{i.title}</h3>
                    <p className="mt-1 text-[#003366]">{i.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </Container>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-[#003366]">
        <Container className="py-14 text-white">
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight">Ready to invest with confidence?</h2>
              <p className="mt-2 max-w-2xl text-white/80">Speak to our team and we’ll guide you every step of the way.</p>
            </div>
            <a href="#/enquire" className="rounded-xl px-5 py-3 text-sm font-semibold bg-white text-[#003366] hover:bg-slate-200">Book a call</a>
          </div>
        </Container>
      </section>
    </main>
  );
}
